import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { networkErrorMessage, useNetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import { NETWORK_BRAND_STATUSES } from '../../../common/hooks/network-admin.types';
import type { NetworkBrand, NetworkBrandStatus } from '../../../common/models/network.model';
import { ErrorNote, FIELD_LABEL } from './network-parts';
import { splitList, vocabKey } from './network-helpers';

/** "New brand": a manufacturer added by hand (most arrive through Discover). */
export function NewBrandDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (open: boolean) => void; onCreated: (brand: NetworkBrand) => void }) {
    const { t } = useTranslation();
    const api = useNetworkAdminApi();
    const [name, setName] = useState('');
    const [country, setCountry] = useState('');
    const [website, setWebsite] = useState('');
    const [description, setDescription] = useState('');
    const [aliases, setAliases] = useState('');
    const [status, setStatus] = useState<NetworkBrandStatus>('unclaimed');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setName('');
        setCountry('');
        setWebsite('');
        setDescription('');
        setAliases('');
        setStatus('unclaimed');
        setError(null);
    }, [open]);

    const create = async () => {
        if (!name.trim()) return;
        setBusy(true);
        setError(null);
        try {
            const brand = await api.createBrand({
                name: name.trim(),
                ...(country.trim() ? { country: country.trim().toUpperCase() } : {}),
                ...(website.trim() ? { website: website.trim() } : {}),
                ...(description.trim() ? { description: description.trim() } : {}),
                ...(splitList(aliases).length ? { aliases: splitList(aliases) } : {}),
                status,
            });
            if (!brand?._id) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
            onOpenChange(false);
            onCreated(brand);
        } catch (e) {
            setError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
            <DialogContent className="sm:max-w-lg" data-testid="new-brand-dialog">
                <DialogHeader>
                    <DialogTitle>{t('ADMIN_NETWORK.NEW_BRAND.TITLE')}</DialogTitle>
                    <DialogDescription>{t('ADMIN_NETWORK.NEW_BRAND.DESCRIPTION')}</DialogDescription>
                </DialogHeader>
                <form
                    className="grid gap-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void create();
                    }}
                >
                    <div className="grid gap-1.5">
                        <Label htmlFor="nb-name" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.NAME')}</Label>
                        <Input id="nb-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required data-testid="new-brand-name" />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="grid gap-1.5">
                            <Label htmlFor="nb-country" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.COUNTRY')}</Label>
                            <Input id="nb-country" value={country} maxLength={2} placeholder="IT" onChange={(e) => setCountry(e.target.value)} />
                        </div>
                        <div className="grid gap-1.5">
                            <Label htmlFor="nb-status" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.STATUS')}</Label>
                            <NativeSelect id="nb-status" className="w-full" value={status} onChange={(e) => setStatus(e.target.value as NetworkBrandStatus)}>
                                {NETWORK_BRAND_STATUSES.map((s) => (
                                    <NativeSelectOption key={s} value={s}>{t(`ADMIN_NETWORK.BRAND_STATUS.${vocabKey(s)}`)}</NativeSelectOption>
                                ))}
                            </NativeSelect>
                        </div>
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="nb-website" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.WEBSITE')}</Label>
                        <Input id="nb-website" type="url" dir="ltr" value={website} placeholder="https://" onChange={(e) => setWebsite(e.target.value)} />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="nb-aliases" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.ALIASES')}</Label>
                        <Input id="nb-aliases" value={aliases} placeholder={t('ADMIN_NETWORK.FIELDS.ALIASES_HINT')} onChange={(e) => setAliases(e.target.value)} />
                    </div>
                    <div className="grid gap-1.5">
                        <Label htmlFor="nb-description" className={FIELD_LABEL}>{t('ADMIN_NETWORK.FIELDS.DESCRIPTION')}</Label>
                        <Textarea id="nb-description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>
                    {error && <ErrorNote message={error} />}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                            {t('ADMIN_SHELL.COMMON.CANCEL')}
                        </Button>
                        <Button type="submit" variant="outline" disabled={busy || !name.trim()} data-testid="new-brand-create">
                            {busy ? t('ADMIN_NETWORK.COMMON.SAVING') : t('ADMIN_NETWORK.NEW_BRAND.CREATE')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
